import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { SecurityProvider } from "@/components/SecurityProvider";
import { AccessibilityProvider } from "@/contexts/AccessibilityContext";
import Index from "./pages/Index";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import ProtectedAdminRoute from "@/components/ProtectedAdminRoute";

// Lazy-load non-index routes to reduce initial JS bundle size
const NotFound = lazy(() => import("./pages/NotFound"));
const Browse = lazy(() => import("./pages/Browse"));
const TitleDetail = lazy(() => import("./pages/TitleDetail"));
const CRRFlash = lazy(() => import("./pages/CRRFlash"));
const CRRZone1 = lazy(() => import("./pages/CRRZone1"));
const CRRZone2 = lazy(() => import("./pages/CRRZone2"));
const CRRZone3 = lazy(() => import("./pages/CRRZone3"));
const CRRZone4 = lazy(() => import("./pages/CRRZone4"));
const CRRQuiz = lazy(() => import("./pages/CRRQuiz"));
const About = lazy(() => import("./pages/About"));
const Contact = lazy(() => import("./pages/Contact"));
const MaritimeCareers = lazy(() => import("./pages/MaritimeCareers"));
const PermisAcademy = lazy(() => import("./pages/PermisAcademy"));
const AdminFlashcards = lazy(() => import("./pages/AdminFlashcards"));
const Install = lazy(() => import("./pages/Install"));
const ScubaDiving = lazy(() => import("./pages/ScubaDiving"));
const DyslexicMarinerProfiles = lazy(() => import("./pages/DyslexicMarinerProfiles"));
const Islands = lazy(() => import("./pages/Islands"));
const BPJEPSJetABras = lazy(() => import("./pages/BPJEPSJetABras"));
const BPJEPSTEPRoadmap = lazy(() => import("./pages/BPJEPSTEPRoadmap"));
const BPJEPSMotonautismeTraining = lazy(() => import("./pages/BPJEPSMotonautismeTraining"));
const BPJEPSMasterBuilder = lazy(() => import("./pages/BPJEPSMasterBuilder"));
const BoatCultureHotspots = lazy(() => import("./pages/BoatCultureHotspots"));
const JetskiOpsPlanner = lazy(() => import("./pages/JetskiOpsPlanner"));
const MeteoMarineGuide = lazy(() => import("./pages/MeteoMarineGuide"));
const BMSBulletinMeteoSpecial = lazy(() => import("./pages/BMSBulletinMeteoSpecial"));
const MeilleureApplicationMeteoMarine = lazy(() => import("./pages/MeilleureApplicationMeteoMarine"));
const Briefings = lazy(() => import("./pages/Briefings"));

const queryClient = new QueryClient();

const App = () => {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <SecurityProvider>
          <AccessibilityProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <Suspense fallback={<div className="min-h-screen bg-background" />}>
                  <Routes>
                    {/* All public routes — no gates */}
                    <Route path="/" element={<Index />} />
                    <Route path="/browse" element={<Browse />} />
                    <Route path="/title/:id" element={<TitleDetail />} />
                    <Route path="/crr-flash" element={<CRRFlash />} />
                    <Route path="/crr-zone1" element={<CRRZone1 />} />
                    <Route path="/crr-zone2" element={<CRRZone2 />} />
                    <Route path="/crr-zone3" element={<CRRZone3 />} />
                    <Route path="/crr-zone4" element={<CRRZone4 />} />
                    <Route path="/crr-quiz" element={<CRRQuiz />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/maritime-careers" element={<MaritimeCareers />} />
                    <Route path="/permis-academy" element={<PermisAcademy />} />
                    <Route path="/admin-flashcards" element={<ProtectedAdminRoute><AdminFlashcards /></ProtectedAdminRoute>} />
                    <Route path="/install" element={<Install />} />
                    <Route path="/scuba-diving" element={<ScubaDiving />} />
                    <Route path="/dt-profiles" element={<DyslexicMarinerProfiles />} />
                    <Route path="/islands" element={<Islands />} />
                    <Route path="/guadeloupe" element={<Islands />} />
                    <Route path="/bpjeps-jet-a-bras" element={<BPJEPSJetABras />} />
                    <Route path="/bpjeps-tep-roadmap" element={<BPJEPSTEPRoadmap />} />
                    <Route path="/bpjeps-motonautisme" element={<BPJEPSMotonautismeTraining />} />
                    <Route path="/bpjeps-master-builder" element={<BPJEPSMasterBuilder />} />
                    <Route path="/boat-culture-hotspots" element={<BoatCultureHotspots />} />
                    <Route path="/jetski-ops-planner" element={<JetskiOpsPlanner />} />
                    <Route path="/meteo-marine-guide" element={<MeteoMarineGuide />} />
                    <Route path="/bms-bulletin-meteo-special" element={<BMSBulletinMeteoSpecial />} />
                    <Route path="/meilleure-application-meteo-marine" element={<MeilleureApplicationMeteoMarine />} />
                    <Route path="/briefings" element={<Briefings />} />
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AccessibilityProvider>
        </SecurityProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
